import {Button, Modal, Space, message} from "antd";
import moment, {Moment} from "moment";
import React, {FC, memo, useEffect, useMemo, useState} from "react";
import {ArrayDataOptions, GetUnpaidScheduledPaymentsQueryVariables, PaymentProvider, TablesEnum, useGetCompanyInfoQuery, useGetUnpaidScheduledPaymentsQuery, useUpdateManyScheduledPaymentIdsMutation} from "../../generated/graphql";
import i18n from "../../services/i18n";
import {rowId, useRowChangeHighlight} from "../../helpers/useRowChangeHighlight";
import CalendarPopover from "../../views/CalendarPopover/CalendarPopover";
import Page from "../../views/Page/Page";
import CreateXmlModal from "./CreateXmlModal/CreateXmlModal";
import PaymentsList, {allColumns} from "./PaymentsList";
import {TableType, MOLLIE_COLUMNS, MolliePaymentLinkDispatchContext, PaymentActionTimelineContext} from "./PaymentsListColumns";
import {PaymentActionTimelineDrawer} from "../../views/PaymentActionTimeline/PaymentActionTimeline";
import {useQuery} from "@apollo/client";
import {DETECT_BUNDLE_CONFLICTS_FROM_ADMIN} from "./mollie.graphql";
import {useLocalStorageOptions} from "../../helpers/columnsLocalStorageHandler";
import {useShowDeletedToggle} from "../../helpers/useShowDeletedToggle";
import UploadSwissQrCamtModal from "../swissQR/UploadSwissQrCamtFile/UploadSwissQrCamtModal";
import Submenu from "./Submenu";
import SuperUserBadge from "../../views/SuperUserBadge/SuperUserBadge";
import CreateDummyCamtFileModal from "../swissQR/UploadSwissQrCamtFile/CreateDummyCamtFileModal";
import ExportPaymentsModal from "./ExportPayments/ExportPaymentsModal";
import {ExportConfig} from "./ExportPayments/types";
import {GET_UNPAID_PAYMENTS} from "./scheduledPayments.graphql";
import {ShowDeletedToggle} from "./ShowDeletedToggle";
import DunningDueToggle from "../invoice/SettlementDetails/BookingDunnings/DunningDueToggle";
import CourseListShowMoreMenu from "../courses/CourseEdit/CourseListShowMoreMenu";
import {MOLLIE_ACTIVE_STATUSES} from "../invoice/SettlementDetails/MolliePaymentSection/helpers/isActiveState";
import {useLiveRefresh} from "../../helpers/useLiveRefresh";

type OpenPaymentType = {
    hideTitle?: boolean;
    customColumns?: Array<Extract<keyof TableType, string>>;
    hidePagination?: boolean;
    tablesEnum?: TablesEnum;
    hideXmlBtn?: boolean;
    style?: React.CSSProperties;
    bookingId?: string;
    hiddenFilters?: Array<Extract<keyof TableType, string>>;
    minPadding?: boolean;
    pollInterval?: number;
    tableTitle?: string | React.ReactNode;
    hideSpecialFunctions?: boolean;
    timeStamp?: number;
    /** Zeitpunkt einer fremden Änderung, die der Buchungsdialog gemeldet hat (Live-Aktualisierung). */
    liveStamp?: number;
    setTimeStamp?: (timeStamp: number) => void;
    hideFooter?: boolean;
    mandatoryColumns?: Array<Extract<keyof TableType, string>>;
    hideExportButton?: boolean;
};

const openPaymentsExportConfig: ExportConfig = {
    title: "Offene Posten exportieren",
    entityLabel: "Offene Posten",
    dateFieldKey: "paymentDue",
    dateFieldTable: "scheduledPayment",
    dateFieldDescription: "Zeitraum bezieht sich auf: Fälligkeitsdatum",
    fileNamePrefix: "Offene-Posten-Export",
    csvTablesEnum: TablesEnum.OpenPaymentsList,
    exportTablesEnum: TablesEnum.OpenPaymentsListExport,
    query: GET_UNPAID_PAYMENTS,
    getItemsFromResult: (data: any) => ({
        items: data.unpaidScheduledPayments?.items ?? [],
        total: data.unpaidScheduledPayments?.total ?? 0,
    }),
    buildVariables: (options: ArrayDataOptions) => ({options, bookingId: null}),
    sortField: "paymentDue",
};

const exportHiddenColumns: Array<Extract<keyof TableType, string>> = [
    "id",
    "setPaidAction",
    "setUnPaidAction",
];

const OpenPaymentsList: FC<OpenPaymentType> = ({
    hideTitle,
    customColumns,
    hidePagination,
    tablesEnum = TablesEnum.OpenPaymentsList,
    hideXmlBtn,
    style,
    bookingId,
    hiddenFilters,
    minPadding,
    pollInterval,
    tableTitle,
    hideSpecialFunctions,
    timeStamp,
    liveStamp,
    setTimeStamp,
    hideFooter = false,
    mandatoryColumns,
    hideExportButton = false,
}) => {
    const localStorageName = tablesEnum;
    const [totalResults, setTotalResults] = useState<number>(0);
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName,
        defaultOptions: {},
        totalResults,
    });
    const [showDeleted, setShowDeleted] = useShowDeletedToggle(tablesEnum);

    // Feature-Flag: Mollie-Spalten nur anzeigen wenn Feature aktiv
    const {data: companyInfoData, loading: companyInfoLoading} = useGetCompanyInfoQuery();
    const featureMollie = !!companyInfoData?.companyInfo?.subscription?.featureMollie;
    const featureDunning = !!companyInfoData?.companyInfo?.subscription?.featureDunning;
    // Filter „Mahnung fällig“ (#1517); bewusst nicht gespeichert, die Liste startet immer vollständig
    const [dunningDue, setDunningDue] = useState(false);


    // GET DATA FOR LIST --------------------------------------------------------------------------------------------------------------------

    const allDefaultColumns: Array<Extract<keyof TableType, string>> = [
        "bookingNumber",
        "courseType",
        "courseNumber",
        "booker",
        "attendee",
        "location",
        "firstCourseLesson",
        "invoice",
        "paymentMethod",
        "paymentSource",
        "mollieStatus",
        "mollieDispatchAction",
        "refundStatus",
        // Phase 16 / AUDIT-02 — Timeline-Drawer-Icon als Row-Action sichtbar per Default.
        "paymentActionTimeline",
        "paymentDue",
        "paymentAmount",
        // B-223 Followup — "Aktiv"/"Gelöscht"-Tag inkl. Restore-Popover.
        "tombstoneStatus",
        "dunningLevel",
        "setPaidAction",
    ];
    // Bewusst NICHT in allDefaultColumns: "mollieLinkExpiresAt" (D-14 LocalStorage-default-hidden).

    const defaultColumns = useMemo(
        () => allDefaultColumns.filter((col) => (featureMollie || MOLLIE_COLUMNS.indexOf(col) === -1) && (featureDunning || col !== "dunningLevel")),
        [featureMollie, featureDunning],
    );

    /**
     * Dynamisches Polling: Wenn Mollie-Zahlungen mit einem aktiven Status
     * (open / pending / authorized) vorhanden sind, aktiviere Polling alle 30
     * Sekunden. Sonst wird der uebergebene pollInterval (oder 0 = kein Polling)
     * verwendet.
     *
     * Single Source of Truth: MOLLIE_ACTIVE_STATUSES wird aus dem isActiveState-
     * Helper importiert (vormals lokal als ["open","pending"] dupliziert, ohne
     * "authorized" — siehe WS-4).
     */
    const MOLLIE_POLL_INTERVAL = 30000;

    const [computedPollInterval, setComputedPollInterval] = useState<number | undefined>(pollInterval);

    const {data, loading, error, refetch} = useGetUnpaidScheduledPaymentsQuery({
        variables: {
            options: options as GetUnpaidScheduledPaymentsQueryVariables["options"],
            bookingId,
            includeDeleted: showDeleted,
            // Archiv-Einstellung des Logins anwenden; der Export fragt ohne und bleibt vollständig
            respectArchiveSetting: true,
            dunningDue: featureDunning && dunningDue,
        },
        pollInterval: computedPollInterval,
        fetchPolicy: "network-only",
    });

    const liveRows = useMemo(() => data?.unpaidScheduledPayments?.items ?? [], [data]);
    const highlight = useRowChangeHighlight<{id: string}>(liveRows, rowId, JSON.stringify([options, bookingId, showDeleted]), data?.unpaidScheduledPayments?.total);
    // Im Buchungsdialog (bookingId) lädt PanelCardPayments alle Listen gemeinsam nach (liveStamp).
    // ponytail: lädt der 30-s-Mollie-Poll eine fremde Änderung vor dem Zähler-Poll, bleibt sie unmarkiert.
    useLiveRefresh(() => highlight.track(refetch, true), Boolean(bookingId), 15000);

    /** Berechne Poll-Interval basierend auf Mollie-Status der geladenen Items */
    useEffect(() => {
        // Wenn ein expliziter pollInterval von aussen gesetzt wurde, hat dieser Vorrang
        if (pollInterval !== undefined) {
            setComputedPollInterval(pollInterval);
            return;
        }

        const items = data?.unpaidScheduledPayments?.items || [];
        // Tombstones (deletedAt != null) duerfen das Polling nicht triggern —
        // ein gesoftdeleter Posten kann zwar noch einen molliePaymentStatus
        // haben, aber sein Lifecycle ist beendet.
        const hasPendingMolliePayment = items.some((item) => {
            return (
                !item.deletedAt &&
                item.paymentProvider === PaymentProvider.Mollie &&
                item.molliePaymentStatus &&
                MOLLIE_ACTIVE_STATUSES.indexOf(item.molliePaymentStatus) !== -1
            );
        });

        setComputedPollInterval(hasPendingMolliePayment ? MOLLIE_POLL_INTERVAL : 0);
    }, [data, pollInterval]);

    React.useEffect(() => {
        refetch();
    }, [timeStamp, refetch]);

    // Fremde Änderung im Buchungsdialog (PanelCardPayments): nachladen und Geändertes markieren.
    React.useEffect(() => {
        if (liveStamp) highlight.track(refetch, true).catch(() => undefined);
    }, [liveStamp, highlight.track, refetch]);

    const handleRefetch = () => {
        setTimeStamp?.(moment().unix());
        refetch();
    };

    useEffect(() => {
        totalResults !== data?.unpaidScheduledPayments?.total && setTotalResults(data?.unpaidScheduledPayments?.total ?? 0);
    }, [data, totalResults]);

    useEffect(() => {
        if (error) {
            message.error(error.message, 3);
        }
    }, [error]);

    // Phase 13 / DISP-08 — Bundle-Konflikt-Detection pro Buchung.
    // Query feuert NUR wenn featureMollie aktiv UND bookingId gesetzt ist
    // (Liste aller Buchungen skippt Konflikt-Check — Backend-Query arbeitet
    // pro Booking; fuer Global-View koennen keine Bundle-Konflikte existieren,
    // weil Bundle-Dispatch nur pro Buchung verfuegbar ist).
    const {data: conflictData} = useQuery<{detectBundleConflictsFromAdmin: Array<string>}>(
        DETECT_BUNDLE_CONFLICTS_FROM_ADMIN,
        {
            variables: {bookingId: bookingId || ""},
            skip: !featureMollie || !bookingId,
            fetchPolicy: "cache-and-network",
        },
    );
    const conflictingSpIds = useMemo(
        () => new Set<string>(conflictData?.detectBundleConflictsFromAdmin ?? []),
        [conflictData],
    );

    // useEffect(() => {
    //     refetch();
    // }, [refetch, tableTitle, timeStamp]);

    const tableData = {
        dataSource: (data?.unpaidScheduledPayments?.items ?? []) as unknown as TableType[],
        existMore: data?.unpaidScheduledPayments?.existsMore ?? false,
        total: data?.unpaidScheduledPayments?.total ?? 0,
        loading,
    };

    // MARK SELECTION OF PAYMENTS ----------------------------------------------------

    const [selection, setSelection] = useState<Array<string>>([]);

    const [updateManyScheduledPaymentMutation] = useUpdateManyScheduledPaymentIdsMutation();

    const setAsPaid = async (date: Moment | null) => {
        const hide = message.loading(i18n.containers.payments.OpenPaymentsList.operate());

        try {
            await updateManyScheduledPaymentMutation({
                variables: {
                    paymentIds: selection,
                    editScheduledPaymentData: {
                        paymentDone: date ? date.toISOString() : moment().toISOString(),
                    },
                },
            });
            // Erfolgsmeldung, Selektion leeren und Refetch NUR bei Erfolg
            message.success(i18n.containers.payments.OpenPaymentsList.markedAsPaidFinished(), 2);
            setSelection([]);
            handleRefetch();
        } catch (error) {
            message.error(error instanceof Error ? error.message : String(error), 3);
        } finally {
            hide();
        }
    };

    /** Führt die Bulk-Bezahlt-Markierung aus */
    const executeBulkPaid = (date: Moment | null) => {
        setAsPaid(date);
    };

    const onOk = (date: Moment | null) => {
        // Prüfe ob Mollie-Payments in der Selektion enthalten sind
        const items = data?.unpaidScheduledPayments?.items || [];
        const selectedMolliePayments = items.filter(
            (item) => selection.indexOf(item.id) !== -1 && item.paymentProvider === PaymentProvider.Mollie,
        );

        if (selectedMolliePayments.length > 0) {
            Modal.confirm({
                title: i18n.containers.payments.PaymentsListColumns.mollieWarning.bulkTitle(),
                content: i18n.containers.payments.PaymentsListColumns.mollieWarning.bulkContent({
                    count: String(selectedMolliePayments.length),
                }),
                okText: i18n.containers.payments.PaymentsListColumns.mollieWarning.okText(),
                cancelText: i18n.containers.payments.PaymentsListColumns.mollieWarning.cancelText(),
                onOk: () => executeBulkPaid(date),
            });
            return;
        }

        executeBulkPaid(date);
    };

    const MarkSelection = () => {
        return (
            <CalendarPopover
                title={i18n.containers.payments.OpenPaymentsList.paymentDate()}
                buttonLeftText={i18n.containers.payments.OpenPaymentsList.changePaymentPopover.cancel()}
                buttonRightClick={(date) => onOk(date)}
                buttonRightText={i18n.containers.payments.OpenPaymentsList.accept()}
            >
                <Button type="primary" size="small">
                    {i18n.containers.payments.OpenPaymentsList.markAsPaid()}
                </Button>
            </CalendarPopover>
        );
    };

    // -------------------------------------------------------------------------------

    // Phase 16 / AUDIT-02 — State für Timeline-Drawer. spId null = Drawer zu.
    const [timelineSpId, setTimelineSpId] = useState<string | null>(null);

    const [xmlBlob, setXmlBlob] = useState<Blob | undefined>();

    useEffect(() => {
        if (xmlBlob) {
            const url = window.URL.createObjectURL(xmlBlob);
            const link = document.createElement("a");

            link.href = url;
            link.setAttribute("download", `SEPA_XML.xml`);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode?.removeChild(link);

            setXmlBlob(undefined);
        }
    }, [xmlBlob]);

    const [createXmlModalOpen, setCreateXmlModalOpen] = useState(false);

    const ActionButtons = () => {
        return (
            <Space>
                <SuperUserBadge>
                    <CreateDummyCamtFileModal openPayments={data?.unpaidScheduledPayments?.items} />
                </SuperUserBadge>
                <UploadSwissQrCamtModal refetch={handleRefetch} />
                <Button size="small" onClick={() => setCreateXmlModalOpen(!createXmlModalOpen)} style={{marginLeft: 7}}>
                    {"Lastschriftexport"}
                </Button>
            </Space>
        );
    };

    return (
        <MolliePaymentLinkDispatchContext.Provider
            value={{
                featureMollie,
                conflictingSpIds,
                refetch: handleRefetch,
            }}
        >
            <PaymentActionTimelineContext.Provider value={{onOpenTimeline: setTimelineSpId}}>
            <CreateXmlModal open={createXmlModalOpen} setOpen={(open) => setCreateXmlModalOpen(open)} now={new Date()} />
            <Page
                data-testid="OpenPaymentsList"
                content={
                    <PaymentsList
                        tableTitle={tableTitle ?? i18n.containers.payments.OpenPaymentsList.tableTitle()}
                    titleHint={highlight.goneCount > 0 && <span className={highlight.goneHintClassName}>{i18n.containers.payments.OpenPaymentsList.liveGone({count: String(highlight.goneCount)})}</span>}
                    rowClassName={highlight.rowClassName}
                        // Leer bis die Flags da sind, sonst übernimmt die Toolbar Standardspalten ohne Mollie/Mahnstufe
                        defaultColumns={customColumns ?? (companyInfoLoading ? [] : defaultColumns)}
                        mandatoryColumns={mandatoryColumns}
                        tableData={tableData}
                        setOptions={setOptions}
                        options={options}
                        refetch={handleRefetch}
                        specialFunctions={hideSpecialFunctions ? undefined : [<MarkSelection key="markSelectionPaid" />]}
                        extraFunctions={[
                            !bookingId && <CourseListShowMoreMenu key="archive-menu" refetch={handleRefetch} />,
                            <ShowDeletedToggle
                                key="show-deleted-toggle"
                                value={showDeleted}
                                onChange={setShowDeleted}
                            />,
                            featureDunning && !bookingId && <DunningDueToggle key="dunning-due-toggle" value={dunningDue} onChange={setDunningDue} />,
                            // Export und Summen kennen den Mahn-Filter nicht; ausblenden statt falsche Zahlen zeigen
                            !hideExportButton && !dunningDue && (
                                <ExportPaymentsModal
                                    key="export-open-payments"
                                    options={options}
                                    columnsToShow={customColumns ?? defaultColumns}
                                    allColumns={allColumns()}
                                    hiddenColumns={exportHiddenColumns}
                                    mandatoryColumns={["paymentDue", "paymentAmount"]}
                                    exportConfig={openPaymentsExportConfig}
                                />
                            ),
                            <Submenu key="submenu" refetch={handleRefetch} />,
                        ].filter(Boolean) as React.ReactNode[]}
                        createXmlBtn={hideXmlBtn ? undefined : <ActionButtons />}
                        selection={(selectedItems: React.SetStateAction<Array<string>>) => setSelection(selectedItems)}
                        tablesEnum={tablesEnum}
                        hidePagination={hidePagination}
                        style={style}
                        hiddenFilters={hiddenFilters}
                        minPadding={minPadding}
                        contentType="openPayment"
                        hideFooter={hideFooter || dunningDue}
                    />
                }
            />
            {/* Phase 16 / AUDIT-02 — Timeline-Drawer, LazyQuery-gated */}
            <PaymentActionTimelineDrawer
                scheduledPaymentId={timelineSpId}
                onClose={() => setTimelineSpId(null)}
            />
            </PaymentActionTimelineContext.Provider>
        </MolliePaymentLinkDispatchContext.Provider>
    );
};

export default memo(OpenPaymentsList);
